import { useMemo, useState } from 'react';
import type { Conversation } from '../lib/conversations';
import type { PageSummary } from '../lib/pages';
import { questionsLeft } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { useAuth } from './AuthProvider';
import { BetaTag, ChevronRight, InitialsAvatar, Roundel } from './brand';

/** Title shown when a conversation has not been summarized yet. */
const UNTITLED = 'Conversation';

interface Group {
  label: string;
  items: Conversation[];
}

/**
 * Buckets conversations by `updatedAt` recency (Today / Yesterday / Previous 7
 * days / Older), each bucket newest-first, to match the existing grouped look.
 */
function groupByRecency(convos: Conversation[]): Group[] {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const dayMs = 86_400_000;
  const todayMs = startOfToday.getTime();

  // Parse each timestamp once and sort newest-first up front, so each bucket
  // stays ordered by push order (no per-bucket re-sort / re-parse).
  const dated = convos
    .map((c) => ({ c, t: new Date(c.updatedAt).getTime() }))
    .sort((a, b) => b.t - a.t);

  const buckets: Record<string, Conversation[]> = { Today: [], Yesterday: [], 'Previous 7 days': [], Older: [] };
  for (const { c, t } of dated) {
    if (Number.isNaN(t) || t < todayMs - 6 * dayMs) buckets.Older.push(c);
    else if (t >= todayMs) buckets.Today.push(c);
    else if (t >= todayMs - dayMs) buckets.Yesterday.push(c);
    else buckets['Previous 7 days'].push(c);
  }
  const order = ['Today', 'Yesterday', 'Previous 7 days', 'Older'];
  return order
    .map((label) => ({ label, items: buckets[label] }))
    .filter((g) => g.items.length > 0);
}

export function Sidebar({
  open,
  onClose,
  onNew,
  conversations,
  onPickHistory,
  onDelete,
  myPages,
  pinnedPages,
  onOpenPage,
  onUnpin,
  onToggleDaily,
  onDeletePage,
  onRenamePage,
  onEditPage,
  activeId,
  liveSessionId,
  busy,
  runningPrompt,
  onOpenProfile,
}: {
  open: boolean;
  onClose: () => void;
  onNew: () => void;
  conversations: Conversation[];
  onPickHistory: (c: Conversation) => void;
  onDelete: (c: Conversation) => void;
  /** The user's own interactive pages ("My Pages"). */
  myPages: PageSummary[];
  /** Pages the user has pinned ("Shared/Pinned"). */
  pinnedPages: PageSummary[];
  onOpenPage: (p: PageSummary) => void;
  onUnpin: (p: PageSummary) => void;
  /** Toggle a My Page's nightly auto-refresh (daily ↔ manual). */
  onToggleDaily: (p: PageSummary) => void;
  /** Delete a My Page (creator-only). */
  onDeletePage: (p: PageSummary) => void;
  /** Rename a My Page (creator-only). */
  onRenamePage: (p: PageSummary) => void;
  /** Edit a My Page with the agent, from a fresh chat (creator-only). */
  onEditPage: (p: PageSummary) => void;
  activeId: string | null;
  /** The session currently on screen (fresh-but-saved chat); its untitled row is
   *  suppressed from history until titled, so it doesn't flicker in mid-turn. */
  liveSessionId: string | null;
  /** A turn is currently streaming (drives the live "active query" row). */
  busy: boolean;
  /** The question that's running, shown in the active-query row. */
  runningPrompt: string | null;
  onOpenProfile: () => void;
}) {
  const { user } = useAuth();
  const [theme, toggleTheme] = useTheme();
  const [query, setQuery] = useState('');
  // Which conversation row's overflow (⋮) menu is open, if any.
  const [menuFor, setMenuFor] = useState<string | null>(null);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Suppress the live session while it's still untitled: it's a fresh chat
    // being created (shown by the "Active" row), so an untitled "Conversation"
    // entry here would just flicker in until the title backfills.
    const visible = conversations.filter((c) => !(c.sessionId === liveSessionId && c.title === null));
    const filtered = q
      ? visible.filter((c) => (c.title ?? UNTITLED).toLowerCase().includes(q))
      : visible;
    return groupByRecency(filtered);
  }, [conversations, liveSessionId, query]);

  if (!user) return null;

  const left = questionsLeft(user);

  return (
    <>
      <div className="he-scrim" style={{ display: open ? 'block' : 'none' }} onClick={onClose} />
      <aside className={'he-sidebar' + (open ? ' open' : '')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '18px 18px 14px' }}>
          <Roundel size={30} />
          <span style={{ fontSize: 19, fontWeight: 700, letterSpacing: '-0.02em' }}>helium</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--he-purple)', border: '1px solid var(--border-1)', padding: '2px 7px', borderRadius: 999 }}>
            AI
          </span>
          <BetaTag />
        </div>

        <div style={{ padding: '0 14px' }}>
          <button
            onClick={onNew}
            className="he-press"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '11px 14px',
              borderRadius: 12,
              border: '1px solid var(--border-1)',
              background: 'var(--bg-1)',
              color: 'var(--fg-1)',
              fontSize: 14.5,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: 18, lineHeight: 1, color: 'var(--he-purple)' }}>＋</span> New chat
          </button>
          <div style={{ position: 'relative', marginTop: 10 }}>
            <input
              placeholder="Search chats"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{
                width: '100%',
                height: 38,
                borderRadius: 10,
                border: '1px solid transparent',
                background: 'var(--bg-3)',
                padding: '0 12px 0 32px',
                fontSize: 13.5,
                fontFamily: 'inherit',
                color: 'var(--fg-1)',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ position: 'absolute', left: 10, top: 11.5 }}>
              <circle cx="11" cy="11" r="7" stroke="var(--fg-3)" strokeWidth="2" />
              <path d="M20 20l-3.5-3.5" stroke="var(--fg-3)" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          {/* GUEST_AUTH (temporary): every guest login gets a fresh, isolated
              identity (see docs/guest-access.md), so chat history AND the page
              sections below empty out on sign-out → sign-in. Set that expectation
              where they'd appear, like the guest-gated "licensed accounts" notices
              in render/pageVisibility.tsx + pageRefresh.tsx. */}
          {user.guest && (
            <div
              role="note"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                marginTop: 10,
                padding: '9px 11px',
                borderRadius: 10,
                background: 'var(--bg-3)',
                fontSize: 12.5,
                lineHeight: 1.45,
                color: 'var(--fg-2)',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0, marginTop: 2, color: 'var(--fg-3)' }}>
                <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
                <path d="M12 11v5M12 7.6h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              <span>Saved chats aren't supported on guest accounts. History and pages clear when you sign out.</span>
            </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 8px 8px', marginTop: 4 }}>
          {busy && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '0 10px 6px' }}>
                Active
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '9px 10px',
                  borderRadius: 9,
                  background: 'var(--bg-3)',
                  fontSize: 13.8,
                  color: 'var(--fg-1)',
                  fontWeight: 500,
                }}
              >
                <span className="he-livedot" aria-hidden />
                <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {runningPrompt || 'Working…'}
                </span>
              </div>
            </div>
          )}
          {/* Discovery sections. Hidden while searching (the box filters chats). */}
          {!query.trim() && (
            <>
              {/* Split the user's own pages: refreshable "Dashboards" (the
                  daily/paused glyph is meaningful) vs one-off static "Pages". */}
              <PageSection label="Dashboards" pages={myPages.filter((p) => p.refreshInterval !== null)} onOpen={onOpenPage} onToggleDaily={onToggleDaily} onDelete={onDeletePage} onRename={onRenamePage} onEdit={onEditPage} />
              <PageSection label="Pages" pages={myPages.filter((p) => p.refreshInterval === null)} onOpen={onOpenPage} onDelete={onDeletePage} onRename={onRenamePage} onEdit={onEditPage} />
              <PageSection label="Saved" pages={pinnedPages} onOpen={onOpenPage} onRemove={onUnpin} />
            </>
          )}
          {groups.map((g) => (
            <div key={g.label} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '0 10px 6px' }}>
                {g.label}
              </div>
              {g.items.map((c) => {
                const isActive = activeId === c.sessionId;
                const menuOpen = menuFor === c.sessionId;
                return (
                  <div key={c.sessionId} className="he-hist-row" style={{ position: 'relative' }}>
                    <button
                      onClick={() => onPickHistory(c)}
                      className="he-hist"
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '9px 32px 9px 10px',
                        borderRadius: 9,
                        border: 'none',
                        cursor: 'pointer',
                        background: isActive ? 'var(--bg-3)' : 'transparent',
                        color: 'var(--fg-1)',
                        fontSize: 13.8,
                        fontFamily: 'inherit',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      {c.title ?? UNTITLED}
                    </button>
                    <button
                      className="he-kebab"
                      aria-label="Conversation options"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuFor(menuOpen ? null : c.sessionId);
                      }}
                      style={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        border: 'none',
                        background: menuOpen ? 'var(--bg-2)' : 'transparent',
                        cursor: 'pointer',
                        padding: 4,
                        borderRadius: 6,
                        lineHeight: 0,
                        color: 'var(--fg-3)',
                        // Revealed on row hover / focus via CSS; forced visible while open.
                        opacity: menuOpen ? 1 : undefined,
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                        <circle cx="12" cy="5" r="1.6" />
                        <circle cx="12" cy="12" r="1.6" />
                        <circle cx="12" cy="19" r="1.6" />
                      </svg>
                    </button>
                    {menuOpen && (
                      <>
                        {/* Click-away layer closes the menu. */}
                        <div onClick={() => setMenuFor(null)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
                        <div
                          role="menu"
                          style={{
                            position: 'absolute',
                            right: 4,
                            top: 'calc(100% - 2px)',
                            zIndex: 21,
                            background: 'var(--bg-1)',
                            borderRadius: 10,
                            padding: 5,
                            minWidth: 152,
                            boxShadow: '0 8px 28px rgba(0,0,0,.16)',
                          }}
                        >
                          <button
                            role="menuitem"
                            className="he-menuitem"
                            onClick={() => {
                              setMenuFor(null);
                              onDelete(c);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 9,
                              width: '100%',
                              textAlign: 'left',
                              padding: '8px 10px',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              borderRadius: 7,
                              color: '#c2403a',
                              fontSize: 13.8,
                              fontFamily: 'inherit',
                            }}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
                              <path
                                d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div style={{ borderTop: '1px solid var(--border-1)', padding: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <button
            onClick={toggleTheme}
            className="he-press"
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '9px 12px',
              borderRadius: 11,
              border: '1px solid var(--border-1)',
              background: 'var(--bg-2)',
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'left',
            }}
          >
            <ThemeIcon dark={theme === 'dark'} />
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{theme === 'dark' ? 'Dark' : 'Light'} mode</span>
          </button>
          <button
            onClick={onOpenProfile}
            className="he-press"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '9px 12px',
              borderRadius: 11,
              border: '1px solid var(--border-1)',
              background: 'var(--bg-2)',
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'left',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M13 2L4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5z" fill="var(--he-purple)" />
            </svg>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{left} left</span>
            <span style={{ fontSize: 11.5, color: 'var(--fg-3)' }}>{user.resetDays > 0 ? `Resets ${user.resetLabel}` : 'Internal'}</span>
          </button>
          <button
            onClick={onOpenProfile}
            className="he-press he-acct"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 6px',
              borderRadius: 11,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'left',
              width: '100%',
            }}
          >
            <InitialsAvatar initials={user.initials} size={34} fontSize={13} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</div>
              <div style={{ fontSize: 12, color: 'var(--fg-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.org}</div>
            </div>
            <ChevronRight size={16} />
          </button>
        </div>
      </aside>
    </>
  );
}

/** Title shown for a page that hasn't been titled. */
const UNTITLED_PAGE = 'Untitled page';

/**
 * A labeled sidebar section listing interactive pages (My Pages / Pinned). Each
 * row opens the page (the standalone wrapper) in a new tab. The "Saved" section
 * (`onRemove`) shows a hover ✕ to remove; the owner sections (Dashboards/Pages,
 * `onEdit`/`onRename`/`onToggleDaily`/`onDelete`) show a ⋮ menu to edit with AI, rename, pause/resume
 * nightly refresh (dashboards only), or delete. Renders nothing when there are
 * no pages, so an empty section is invisible.
 */
function PageSection({
  label,
  pages,
  onOpen,
  onRemove,
  onToggleDaily,
  onDelete,
  onRename,
  onEdit,
}: {
  label: string;
  pages: PageSummary[];
  onOpen: (p: PageSummary) => void;
  onRemove?: (p: PageSummary) => void;
  onToggleDaily?: (p: PageSummary) => void;
  onDelete?: (p: PageSummary) => void;
  onRename?: (p: PageSummary) => void;
  onEdit?: (p: PageSummary) => void;
}) {
  const [menuFor, setMenuFor] = useState<string | null>(null);
  if (pages.length === 0) return null;
  const hasMenu = !!(onDelete || onToggleDaily || onRename || onEdit);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '0 10px 6px' }}>
        {label}
      </div>
      {pages.map((p) => {
        const menuOpen = menuFor === p.id;
        const daily = p.refreshInterval === 'daily';
        return (
          <div key={p.id} className="he-hist-row" style={{ position: 'relative' }}>
            <button
              onClick={() => onOpen(p)}
              className="he-hist"
              title={p.title ?? UNTITLED_PAGE}
              style={{
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: onRemove || hasMenu ? '9px 32px 9px 10px' : '9px 10px',
                borderRadius: 9,
                border: 'none',
                cursor: 'pointer',
                background: 'transparent',
                color: 'var(--fg-1)',
                fontSize: 13.8,
                fontFamily: 'inherit',
              }}
            >
              <PageGlyph paused={p.paused} interval={p.refreshInterval} />
              <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title ?? UNTITLED_PAGE}</span>
            </button>

            {onRemove && !hasMenu && (
              <button
                className="he-kebab"
                aria-label="Remove from saved"
                title="Remove from saved"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(p);
                }}
                style={kebabStyle}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            )}

            {hasMenu && (
              <button
                className="he-kebab"
                aria-label="Page options"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuFor(menuOpen ? null : p.id);
                }}
                style={{ ...kebabStyle, background: menuOpen ? 'var(--bg-2)' : 'transparent', opacity: menuOpen ? 1 : undefined }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <circle cx="12" cy="5" r="1.6" />
                  <circle cx="12" cy="12" r="1.6" />
                  <circle cx="12" cy="19" r="1.6" />
                </svg>
              </button>
            )}

            {hasMenu && menuOpen && (
              <>
                <div onClick={() => setMenuFor(null)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
                <div role="menu" style={{ position: 'absolute', right: 4, top: 'calc(100% - 2px)', zIndex: 21, background: 'var(--bg-1)', borderRadius: 10, padding: 5, minWidth: 176, boxShadow: '0 8px 28px rgba(0,0,0,.16)' }}>
                  {onEdit && (
                    <button
                      role="menuitem"
                      className="he-menuitem"
                      onClick={() => {
                        setMenuFor(null);
                        onEdit(p);
                      }}
                      style={menuItemStyle}
                    >
                      Edit with AI
                    </button>
                  )}
                  {onRename && (
                    <button
                      role="menuitem"
                      className="he-menuitem"
                      onClick={() => {
                        setMenuFor(null);
                        onRename(p);
                      }}
                      style={menuItemStyle}
                    >
                      Rename
                    </button>
                  )}
                  {onToggleDaily && p.refreshInterval !== null && (
                    <button
                      role="menuitem"
                      className="he-menuitem"
                      onClick={() => {
                        setMenuFor(null);
                        onToggleDaily(p);
                      }}
                      style={menuItemStyle}
                    >
                      {daily ? 'Pause daily refresh' : 'Refresh daily'}
                    </button>
                  )}
                  {onDelete && (
                    <button
                      role="menuitem"
                      className="he-menuitem"
                      onClick={() => {
                        setMenuFor(null);
                        onDelete(p);
                      }}
                      style={{ ...menuItemStyle, color: '#c2403a' }}
                    >
                      Delete page
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

const kebabStyle: React.CSSProperties = {
  position: 'absolute',
  right: 4,
  top: '50%',
  transform: 'translateY(-50%)',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: 4,
  borderRadius: 6,
  lineHeight: 0,
  color: 'var(--fg-3)',
};

const menuItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  width: '100%',
  textAlign: 'left',
  padding: '8px 10px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: 7,
  color: 'var(--fg-1)',
  fontSize: 13.8,
  fontFamily: 'inherit',
};

/** Small leading glyph for a page row: a document mark, tinted + labelled by
 *  refresh state. Paused (a failed daily page needing attention) adds an "!"
 *  mark so the state isn't conveyed by color alone (a11y), plus a tooltip/
 *  aria-label so the color's meaning is legible to everyone. */
function PageGlyph({ paused, interval }: { paused: boolean; interval: PageSummary['refreshInterval'] }) {
  const color = paused ? 'var(--he-orange)' : interval === 'daily' ? 'var(--he-purple)' : 'var(--fg-3)';
  const label = paused
    ? 'Auto-refresh paused — needs attention'
    : interval === 'daily'
      ? 'Refreshes daily'
      : interval === 'manual'
        ? 'Manual refresh'
        : 'Static page';
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }} role="img" aria-label={label}>
      <title>{label}</title>
      <path d="M6 3h8l4 4v14H6z" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M14 3v4h4" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
      {paused && <path d="M11 11v3.5M11 17h.01" stroke={color} strokeWidth="2" strokeLinecap="round" />}
    </svg>
  );
}

/** Sun (light) / moon (dark) glyph for the theme toggle, tinted to the accent. */
function ThemeIcon({ dark }: { dark: boolean }) {
  return dark ? (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" fill="var(--he-purple)" />
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--he-purple)" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="4.2" fill="var(--he-purple)" stroke="none" />
      <g strokeLinecap="round">
        <path d="M12 2.5v2.6M12 18.9v2.6M21.5 12h-2.6M5.1 12H2.5M18.7 5.3l-1.8 1.8M7.1 16.9l-1.8 1.8M18.7 18.7l-1.8-1.8M7.1 7.1 5.3 5.3" />
      </g>
    </svg>
  );
}
